/**
 * caseStudies.js — structured, code-managed case-study records.
 *
 * Each record is the EVIDENCE version of a project — separate from
 * `/portfolio` (a broader gallery) and `/products/:slug` (marketing).
 *
 *   status === "published"  →  rendered publicly + included in sitemap
 *   status === "draft"      →  hidden from every public surface
 *
 * Rule 2 (do-not-fabricate) applies to every field. Metrics are NOT stored
 * here — they live in projectMetrics.js and are referenced by id.
 *
 * Fields:
 *   slug                   /case-studies/{slug}
 *   status                 "published" | "draft"
 *   projectName            display name
 *   shortDescription       one-line summary used on cards + meta
 *   heroSubtitle           longer hero paragraph
 *   externalUrl            live product URL if publicly linkable
 *   heroImage              featured image (uses productConfigs where verified)
 *   industry               "education" | ... (single canonical industry slug)
 *   solutionType           short human string ("AI-native CRM", "AI teaching OS")
 *   platform               short human string ("Web SaaS", "Web · iOS · Android")
 *   services[]             Phase 3.1 service slugs where the case is genuine evidence
 *   markets[]              market slugs — leave [] until owner-verified
 *   technologies[]         verified tech / integrations list
 *   cloudLabRole[]         verified areas Cloud Lab delivered
 *   challenge              plain-English problem the system solves
 *   solution               plain-English description of what Cloud Lab built
 *   capabilities[]         { title, desc } — project-specific (do not copy service-page items)
 *   workflow[]             ordered steps { title, desc } — reflect the real product
 *   metricIds[]            references into projectMetrics.js
 *   media[]                { src, alt, caption? } — only real assets
 *   relatedProjects[]      other case-study slugs to cross-link
 *   seo                    { title, description, ogImage }
 */

export const CASE_STUDIES = [
  {
    slug: "unicrm-ai",
    status: "published",
    projectName: "UniCRM.ai",
    shortDescription:
      "AI-enabled CRM and recruitment workflow platform for education consultancies, university partners and admissions BPOs.",
    heroSubtitle:
      "UniCRM.ai gives education recruitment teams a WhatsApp-first inbox, three specialised AI agents, a desk-based application workflow and an AI document-vetting engine — so counsellors can own more students without dropping quality.",
    externalUrl: "https://unicrm.ai/",
    heroImage:
      "https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=1600&q=80",
    industry: "education",
    solutionType: "AI-native admissions CRM",
    platform: "Web SaaS · multi-workspace",
    services: [
      "ai-development",
      "ai-agent-development",
      "crm-development",
      "saas-development",
      "web-application-development",
      "edtech-development",
      "custom-software-development",
    ],
    markets: [
      // Deliberately empty until each market is owner-verified.
      // Adding a slug here (e.g. "uk") will automatically surface the case
      // study on that market page via RelatedCaseStudies.
    ],
    technologies: [
      "React",
      "Next.js",
      "FastAPI",
      "MongoDB",
      "WhatsApp Business API",
      "Facebook Lead Ads",
      "OpenAI GPT",
      "Retrieval-Augmented Generation",
      "Role-Based Access Control",
      "Multi-tenant workspaces",
    ],
    cloudLabRole: [
      "Product engineering",
      "Architecture",
      "Frontend development",
      "Backend development",
      "AI agent integration",
      "AI document-vetting pipeline",
      "Ongoing development",
    ],
    challenge:
      "Education recruitment consultancies run on WhatsApp, spreadsheets and desk-to-desk handovers. A single counsellor is expected to shepherd hundreds of students from enquiry through document collection, application, visa and enrolment — with fragmented tools, unverified paperwork and no single view of a student. The bottleneck is not lead volume; it is the operational load per lead. Documents arrive in dozens of formats, chase-ups happen inside personal chats and applications stall waiting for a passport photo or a missing English certificate.",
    solution:
      "Cloud Lab designed and shipped UniCRM.ai as a purpose-built admissions CRM. Every enquiry — from Meta Lead Ads, web forms, an inbound API or the WhatsApp Business inbox — lands in a single funnel and is routed automatically. Three specialised AI agents (Counsellor, Admissions, Customer Care) sit inside each workspace and are trained on the consultancy's own programmes and processes. A desk-based workflow moves each application across Counsellor → Admissions → Finance → Visa with role-based access, and an AI document-vetting engine extracts passports, transcripts and English certificates to flag missing pages before submission. BPO partners get their own portal with KPIs, billing and audit trail, so consultancies can scale overflow without losing control.",
    capabilities: [
      {
        title: "Unified WhatsApp inbox",
        desc: "Two-way WhatsApp Business threads with AI-suggested replies, broadcasts and automated lead routing — one inbox per workspace, unified per student.",
      },
      {
        title: "Three specialised AI agents",
        desc: "Counsellor (course match & eligibility), Admissions (application build) and Customer Care (status) — each trained on the consultancy's programmes and SOPs.",
      },
      {
        title: "AI document vetting",
        desc: "Auto-extracts passports, transcripts and English certificates. Detects missing pages, expiry dates and mismatched names before an application is submitted.",
      },
      {
        title: "Desk-based application workflow",
        desc: "Every student moves across Counsellor, Admissions, Finance and Visa desks with role-based access, SLAs per desk and full audit trail.",
      },
      {
        title: "Lead integration hub",
        desc: "Facebook Lead Ads, Google Sheets, web forms and an inbound API — every enquiry lands in the same funnel with UTM attribution preserved.",
      },
      {
        title: "BPO services module",
        desc: "White-label admissions support: assign overflow to BPO teams with their own portal, KPIs and billing — multi-tenant isolation guaranteed.",
      },
      {
        title: "Universities catalog",
        desc: "Live programmes, deadlines and eligibility rules — always current, referenced by the AI Counsellor when matching students.",
      },
      {
        title: "Insights & reporting",
        desc: "Custom report builder, team KPIs, country/programme funnels, conversion analytics and forecasts.",
      },
    ],
    workflow: [
      { title: "Lead capture", desc: "Enquiry lands from ads, forms or WhatsApp — attributed and routed automatically." },
      { title: "AI-assisted qualification", desc: "The AI Counsellor matches programmes and checks basic eligibility." },
      { title: "Counsellor desk", desc: "Human counsellor takes ownership and briefs the student." },
      { title: "Document vetting", desc: "AI extracts and validates documents — missing pages flagged before submission." },
      { title: "Application build", desc: "The Admissions agent drafts programmes, personal statements and financial forms." },
      { title: "Finance & visa desks", desc: "Payments, offers and visa preparation move across desks with SLAs." },
      { title: "Enrolment & reporting", desc: "Enrolment logged, commissions tracked, cohort reporting closed out." },
    ],
    metricIds: [
      "unicrm-document-vetting-time",
      "unicrm-counsellor-load-ratio",
      "unicrm-pilot-leads-handled",
      "unicrm-ai-agents-per-workspace",
      "unicrm-whatsapp-availability",
    ],
    media: [
      {
        src: "https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=1600&q=80",
        alt: "UniCRM.ai admissions dashboard concept",
      },
    ],
    relatedProjects: ["teachingsoftware-ai"],
    seo: {
      title: "UniCRM.ai Case Study · AI CRM & Recruitment Automation",
      description:
        "How Cloud Lab designed and shipped UniCRM.ai — an AI-native admissions CRM with WhatsApp automation, three specialised agents and AI document vetting for education consultancies.",
      ogImage:
        "https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=1600&q=80",
    },
  },

  {
    slug: "teachingsoftware-ai",
    status: "published",
    projectName: "TeachingSoftware.ai",
    shortDescription:
      "AI teaching OS that plans classes, generates quizzes, runs live sessions and grows tuition businesses.",
    heroSubtitle:
      "TeachingSoftware.ai is the AI teaching operating system Cloud Lab built for private tutors and tuition businesses — one place to build classes, generate lesson plans and quizzes, run live sessions and manage subscribers.",
    externalUrl: "https://teachingsoftware.ai/",
    heroImage:
      "https://images.unsplash.com/photo-1584697964192-c9d3d11a15c7?auto=format&fit=crop&w=1600&q=80",
    industry: "education",
    solutionType: "AI teaching OS · EdTech SaaS",
    platform: "Web SaaS",
    services: [
      "ai-development",
      "saas-development",
      "web-application-development",
      "edtech-development",
      "custom-software-development",
    ],
    markets: [
      // Deliberately empty — markets flagged for owner verification.
    ],
    technologies: [
      "React",
      "Next.js",
      "FastAPI",
      "MongoDB",
      "OpenAI GPT",
      "Live session tooling",
      "Subscription billing",
    ],
    cloudLabRole: [
      "Product engineering",
      "Architecture",
      "Frontend development",
      "Backend development",
      "AI integration",
    ],
    challenge:
      "Independent tutors and small tuition businesses juggle a stack of point tools — one for scheduling, another for lesson plans, another for quizzes, another for live delivery, another for payments. Nothing knows about the student or the curriculum, so every lesson is rebuilt from scratch and every business decision is made without data. Growing beyond a founder-tutor into a team is where the model breaks.",
    solution:
      "Cloud Lab built TeachingSoftware.ai as an integrated teaching operating system. Tutors define their subject and curriculum once, and the platform's AI generates lesson plans and quizzes automatically. Live sessions run inside the same product — no context-switch — and every student's progress is retained across lessons. A subscriber-management layer lets a tutor operate as a small business: pricing, billing, cohorts and reporting all live in one place.",
    capabilities: [
      {
        title: "AI lesson planning",
        desc: "Generate structured lesson plans from a topic and student level — editable, reusable and mapped to the tutor's curriculum.",
      },
      {
        title: "AI quiz generation",
        desc: "One-click quizzes with automatic grading and per-student feedback.",
      },
      {
        title: "Live session tooling",
        desc: "Run live classes inside the same product — no separate video app to launch.",
      },
      {
        title: "Class & cohort builder",
        desc: "Group students into cohorts, schedule sessions and reuse plans across cohorts.",
      },
      {
        title: "Subscriber management",
        desc: "Pricing, subscriptions and cohort access managed inside the same platform.",
      },
    ],
    workflow: [
      { title: "Define subject", desc: "Tutor sets their subject, curriculum and preferred level." },
      { title: "AI generates plan", desc: "Lesson plan and quizzes are generated and edited to taste." },
      { title: "Run live session", desc: "Class is delivered live, inside the same platform." },
      { title: "Track progress", desc: "Per-student progress and quiz results are retained across lessons." },
      { title: "Grow subscribers", desc: "Pricing, billing and cohort access are managed together." },
    ],
    metricIds: [
      // Deliberately empty — no TeachingSoftware.ai metric has been
      // owner-approved for public display yet. Metrics section will be
      // hidden until PROJECT_METRICS gains an approved entry.
    ],
    media: [
      {
        src: "https://images.unsplash.com/photo-1584697964192-c9d3d11a15c7?auto=format&fit=crop&w=1600&q=80",
        alt: "TeachingSoftware.ai teaching dashboard concept",
      },
    ],
    relatedProjects: ["unicrm-ai"],
    seo: {
      title: "TeachingSoftware.ai Case Study · EdTech SaaS Development",
      description:
        "How Cloud Lab designed and shipped TeachingSoftware.ai — an AI teaching operating system for private tutors and tuition businesses: lesson planning, quizzes, live sessions and subscriber management.",
      ogImage:
        "https://images.unsplash.com/photo-1584697964192-c9d3d11a15c7?auto=format&fit=crop&w=1600&q=80",
    },
  },

  // Nexus One — deliberately not published (rule 2). Kept as an internal
  // draft slot so the architecture is ready when the project data has been
  // owner-verified. Draft records are hidden from every public surface.
  {
    slug: "nexus-one",
    status: "draft",
    projectName: "Nexus One",
    shortDescription: "Draft record — pending owner verification.",
    industry: "construction",
    services: [],
    markets: [],
    technologies: [],
    metricIds: [],
    media: [],
    seo: {
      title: "Nexus One (draft)",
      description: "Draft record — not indexed.",
    },
  },
];

// ---------------- helpers --------------------------------------------------
export function getPublishedCaseStudies() {
  return CASE_STUDIES.filter((cs) => cs.status === "published");
}

export function getCaseStudyBySlug(slug) {
  return CASE_STUDIES.find((cs) => cs.slug === slug) || null;
}

/**
 * Filter published case studies by service / market / industry.
 * Every filter is optional; returns everything published when no filter given.
 */
export function findCaseStudies({ service, market, industry } = {}) {
  return getPublishedCaseStudies().filter((cs) => {
    if (service && !(cs.services || []).includes(service)) return false;
    if (market && !(cs.markets || []).includes(market)) return false;
    if (industry && cs.industry !== industry) return false;
    return true;
  });
}
